import { FormikErrors } from "formik"
import useTranslation from "next-translate/useTranslation"
import { useEffect, useState } from "react"
import { Row } from "reactstrap"

import { ButtonProps, FormButtonArea } from "./FormButtonArea"
import GeneralFormError from "./GeneralFormError"

/**
 * id of the error and button container div
 */
export const ERROR_AND_BUTTON_CONTAINER_ID = "error-and-buttons-container"

interface IProps {
  className?: string
  /**
   * By default the cancel button works as a history back button, @see FormButtonArea .
   * Overwrite this if the user should be sent somewhere else,
   * e.g. when the form must be closed instead of routing the user
   *
   * set cancelButton={null} to NOT show a cancel button
   */
  cancelButton?: ButtonProps
  /**
   * A default form button to reset form content.
   */
  resetButton?: ButtonProps
  submitButton?: ButtonProps
  /**
   * fields that should be handled individually, not via general error handling
   *
   * @see /doc/forms.md -> Komponenten für den Umgang mit Fehlern
   */
  fieldsToExcludeFromBeingShownAsGeneralError?: string[]
  /**
   * nested errors must have a prefix to be dicriminated from each other
   * it is used to discriminate the labelling of the shown error message
   * e.g. "prifix" on { input1: "failure.errormessage" } leads to visible output:
   * prifix.input1.label: error:failure.errormessage
   * with a prefix "project" it is discriminated from an error with prefix "challenge":
   * project.input1.label: ...   vs. challenge.input1.label
   */
  translationPrefixOnNestedErrors: string
  /**
   * nested errors may get a different suffix than "label" (default)
   * to choose the wanted error translation
   * e.g. "suffyx" on { input1: "failure.errormessage" } leads to visible output:
   * prefix.input1.suffyx: error:failure.errormessage
   *
   * used to get description text to the relevant input form, e.g. "Measurement of the goal"
   * Together with the error message it reads: "Measurement of the goal: The entered text is too short."
   */
  translationSuffixOnNestedErrors?: string
  /**
   * used format:
   *
   * @see IValidationErrors
   */
  errors: FormikErrors<any>
  isSubmitting: boolean
  isValidating: boolean
}

/**
 * This function produces the "name" of the first error in a given error-list
 * in the form "goals[2].measurability" or just "shortDescription".
 * This form of name is to be used by document.getElementsByName() to identify the element
 * by this name.
 *
 * It may be used to jump the element, when an error occured, e.g.:
 * document.getElementsByName(fullFirstErrorName(errors))[0]?.scrollIntoView()
 *
 * @param errors FormikErrors that occure
 * @returns the full name of the first error in a list of FormikErrors
 */
export const fullFirstErrorName = (errors: FormikErrors<any>): string => {

  // variants of errors:
  // single: "title"
  // array: "impact.2" (may also be an object -> coder-dependent)
  // multi-array as object: "goals[2].measurability"

  if (!errors || errors === undefined) {
    return
  }
  // key of the first error in the givenerror-array
  const firstErrorKey = Object.keys(errors)[0]
  if (!firstErrorKey) {
    return
  }
  // if the value to that key is a string, its a "flat" error, so the firstErrorKey is the error name...
  if (typeof errors[firstErrorKey] === "string") {
    return firstErrorKey
  }

  if (Array.isArray(errors[firstErrorKey])) {
    // may be an array, if a coder defines it as array, not as object
    // ... otherwise there are sub-errors from FormikFieldArray
    const firstError: string[] = errors[firstErrorKey] as string[]
    const elementIndex = firstError[0]
    // if the sub-error is "flat", the errorName has the format name.index, e.g.: "impact.2"
    return firstErrorKey + "." + elementIndex
  }

  if (typeof errors[firstErrorKey] === "object") {
    // if the sub-error is not "flat", its a MultiFieldArray with three levels
    // OR a string-indexed error object
    // attention: may also be an array

    const firstError: Record<string, any> = errors[firstErrorKey] as Record<string, any>
    // get the index of the first element in that error-sub-array
    const elementIndex = Object.keys(firstError)[0]
    // because the error could occur in the third element of that array
    const subError = firstError[elementIndex]

    if (typeof subError === "string") {
      // its an array, although it looks like an object
      return firstErrorKey + "." + elementIndex
    }

    // else get the key of that first sub-error
    const subElementKey = Object.keys(subError as [])[0]
    // build the complete errorName, format name[index].subname, e.g. "goals[2].measurability"
    return firstErrorKey + "[" + elementIndex + "]." + subElementKey
  }

  return undefined
}

/**
 * Displays general errors and buttons for a form,
 * e.g. errors coming from submissions to the backend - those will be set by setErrors() in the SAGA.
 * General errors are displayed near the save button.
 * Mostly it should not display field specific errors. Thats why those are excluded.
 * Field specific errors should be displayed near the erroneous field and the browser should scroll to this field
 * to let the user handle the error.
 *
 * The component must be placed as child within a formik form to receive data of the formik form data (errors, values, isSubmitting, isValidating).
 * It is recommended to place this component at the top and the bottom of larger forms
 * (so the user can easily find buttons and errors) and at the bottom of smaller forms.
 *
 * @see /doc/forms.md -> Komponenten für den Umgang mit Fehlern
 */
const FormButtonsAndGeneralErrorArea = (props: IProps): JSX.Element => {
  const {
    className,
    fieldsToExcludeFromBeingShownAsGeneralError = [],
    translationPrefixOnNestedErrors,
    translationSuffixOnNestedErrors = "label",
    errors,
    isSubmitting,
    isValidating,
    submitButton,
    cancelButton,
    resetButton
  } = props
  const { t } = useTranslation("common")

  // state to store, if this component is the sender of the submit-signal
  // to only once trigger the jump to the error-element, when the FormButtonsAndGeneralErrorArea
  // is used more than once in forms
  // @todo: reset the state, because the user may change the used button
  const [isSubmitSender, setIsSubmitSender] = useState(false)

  const scrollIntoViewFirstError = () => {
    // check, if the application element is running on the server or in the browser currently => window is just in the browser defined
    if (typeof window !== undefined) {
      const firstErrorName = fullFirstErrorName(errors)
      // The formik components are identified by the name
      // But it may not be supported (suspect: RTE/reactQuill),
      // so identify the element alternatively by its ID
      // NOTE: for tests, mock scrollIntoView by: window.HTMLElement.prototype.scrollIntoView = jest.fn()
      if (document.getElementsByName(firstErrorName).length > 0) {
        // scroll to the first found element
        document.getElementsByName(firstErrorName)[0]?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
      } else {
        document.getElementById(firstErrorName)?.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
      }
    }
  }

  useEffect(() => {
    // If during the validation process are any errors found or the submission was successful, isValidating and isSubmitting is set to false
    // If isValidating and isSubmitting is set to false, it mean's the validation process was running (or was not started yet).
    // After that, the component can check for errors, cause it's important, that validation has been run and therefor the errors have been updated.
    // For more information about formik submission life-cycle look at: https://formik.org/docs/guides/form-submission.
    // But in both use cases (we can't differentiate between both use cases and it is not important),
    // the component is checking for errors and if one or more errors are found, will jump to the first error
    if (!isSubmitting && !isValidating && isSubmitSender && errors && Object.keys(errors).length > 0) {
      // get the first element with the name of the error-producing element
      // and scroll the users browser window there
      scrollIntoViewFirstError()
    }
    // NOTE: listening to isSubmitSender is necessary in tests only to trigger (and test) the scrolling
    // b/c isValidating and isSubmitting are given as unchangeable prop from outside
    // @todo is there a way to trigger this for tests without listening to isSubmitSender?
  }, [isValidating, isSubmitting, isSubmitSender])

  const realSubmitButton: ButtonProps = {
    // use this title by default
    title: t("form.saveChanges"),
    // overwrite props by given props
    ...submitButton,
    // overwrite onClick
    onClick: () => {
      // use onClick if given
      if (submitButton?.onClick) {
        submitButton.onClick()
      }
      // inject the isSubmitSender marking function into the onClick function
      setIsSubmitSender(true)
    }
  }

  /**
   * if the provided cancelButton is set to null it is not shown
   * otherwise it is shown with default values of FormButtonArea
   */
  const realCancelButton: ButtonProps = cancelButton === null
    ? null
    : { ...cancelButton } // may be an empty object if cancelButton is undefined; then FormButtonArea uses its defaults for cancel button

  return <Row id={ERROR_AND_BUTTON_CONTAINER_ID} className={className ? className : " align-right"}>
    <div id="errorMessage" onClick={() => {
      scrollIntoViewFirstError()
    }}>
      {Object.keys(errors).length > 0 && <GeneralFormError
        errors={errors}
        prefix={translationPrefixOnNestedErrors}
        suffixForErrorTitle={translationSuffixOnNestedErrors}
        excludeFields={fieldsToExcludeFromBeingShownAsGeneralError}
        onClick={scrollIntoViewFirstError}
      />
      }
    </div>
    <FormButtonArea
      isSubmitting={isSubmitting}
      submitButton={realSubmitButton}
      cancelButton={realCancelButton}
      resetButton={resetButton}
    // no remove button
    />

    {/* // just a design study: no decision if used, FCP-992
    errors &&
            <Tooltip target="errorMessage" toggle={toggleTooltip} isOpen={tooltipOpen}>
                {t("goto.jumpToError")}
            </Tooltip>
        */}
  </Row>
}

export default FormButtonsAndGeneralErrorArea